import React from 'react';
import {
  AlignHorizontalJustifyStartIcon,
  AlignHorizontalJustifyCenterIcon,
  AlignHorizontalJustifyEndIcon,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  FlipHorizontal2,
  FlipVertical2,
  Maximize2,
  Trash2,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

import ColorSwatchPicker from './ColorSwatchPicker';

import arrowUpRound from '../../assets/images/icon-left-round.png'

export const LeftPanelField = ({ label, children, rightContent }) => (
  <div className="control-group">
    <div className="control-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>{label}</span>
      {rightContent}
    </div>
    {children}
  </div>
);

export const TextToolsPanel = ({
  textId,
  item,
  disabled,
  onAlignHorizontal,
  onAlignVertical,
  onFlip,
  onBestFit,
  onDelete,
}) => {
  const alignButtons = [
    { Icon: AlignHorizontalJustifyStartIcon, title: 'Align left', action: () => onAlignHorizontal(textId, 'left') },
    { Icon: AlignHorizontalJustifyCenterIcon, title: 'Align center', action: () => onAlignHorizontal(textId, 'center') },
    { Icon: AlignHorizontalJustifyEndIcon, title: 'Align right', action: () => onAlignHorizontal(textId, 'right') },
    { Icon: AlignVerticalJustifyStart, title: 'Align top', action: () => onAlignVertical(textId, 'top') },
    { Icon: AlignVerticalJustifyCenter, title: 'Align middle', action: () => onAlignVertical(textId, 'center') },
    { Icon: AlignVerticalJustifyEnd, title: 'Align bottom', action: () => onAlignVertical(textId, 'bottom') },
  ];

  return (
    <div className="control-group">
      <div className="control-label">Tools</div>
      <div className="text-style-buttons">
        {alignButtons.map((btn, i) => {
          const Icon = btn.Icon;
          return (
            <button
              key={i}
              type="button"
              className="toggle-button"
              disabled={disabled}
              title={btn.title}
              onClick={btn.action}
            >
              <Icon size={16} color="#80808082" />
            </button>
          );
        })}
        <button
          type="button"
          className={`toggle-button ${item?.flipH ? 'active' : ''}`}
          disabled={disabled}
          onClick={() => onFlip(textId, 'flipH', !item.flipH)}
        >
          <FlipHorizontal2 color={item?.flipH ? '#2563eb' : '#80808082'} width={20} height={20} />
        </button>
        <button
          type="button"
          className={`toggle-button ${item?.flipV ? 'active' : ''}`}
          disabled={disabled}
          onClick={() => onFlip(textId, 'flipV', !item.flipV)}
        >
          <FlipVertical2 color={item?.flipV ? '#2563eb' : '#80808082'} width={20} height={20} />
        </button>
        <button
          type="button"
          className="toggle-button"
          disabled={disabled}
          title="Best Fit"
          onClick={() => onBestFit(textId)}
        >
          <Maximize2 size={14} color="#80808082" />
        </button>
        <button
          type="button"
          className="toggle-button"
          disabled={disabled}
          onClick={() => onDelete(textId)}
        >
          <Trash2 size={14} style={{ marginRight: 6 }} color='red' />
        </button>
      </div>
    </div>
  );
};

export const CurveControl = ({
  textId,
  curve,
  arcDirection,
  disabled,
  onToggleCurve,
  onSetDirection,
}) => (
  <div className="control-group">
    <div className="control-label">Curve</div>
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: !textId ? 'not-allowed' : 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        if (textId) e.target.style.backgroundColor = 'rgba(37, 99, 235, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = 'transparent';
      }}
      title="Enable curved text effect"
    >
      <input
        type="checkbox"
        checked={curve || false}
        disabled={disabled}
        onChange={(e) => onToggleCurve(textId, e.target.checked)}
        style={{
          width: 16,
          height: 16,
          cursor: disabled ? 'not-allowed' : 'pointer',
          accentColor: '#2563eb',
        }}
      />
      <span
        style={{
          fontSize: '12px',
          fontWeight: '500',
          userSelect: 'none',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        Curve
      </span>
    </label>

    {curve && textId && (
      <div style={{ marginTop: 8, paddingLeft: 8 }}>
        <div className="control-group">
          <div className="control-label">Direction</div>
          <div className="text-style-buttons" style={{ gap: 4 }}>
            <button
              type="button"
              className={'toggle-button' + ((arcDirection || 'up') === 'up' ? ' active' : '')}
              onClick={() => onSetDirection(textId, 'up')}
              title="Arc upward"
            >
              <img src={arrowUpRound} height={14} width={24}  />
              {/* <ArrowUp size={18} /> */}
            </button>
            <button
              type="button"
              className={'toggle-button' + (arcDirection === 'down' ? ' active' : '')}
              onClick={() => onSetDirection(textId, 'down')}
              title="Arc downward"
            >
              <img src={arrowUpRound} height={14} width={24} style={{ rotate: '180deg' }}/>
              {/* <ArrowDown size={18} /> */}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);

export const FontSelect = ({ value, disabled, onChange }) => (
  <div className="control-group">
    <div className="control-label">Font</div>
    <select
      className="form-input"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option>Arial</option>
      <option>Helvetica</option>
      <option>Impact</option>
      <option>Georgia</option>
      <option>Times New Roman</option>
      <option>Verdana</option>
      <option>Roboto</option>
      <option>Open Sans</option>
      <option>Lato</option>
      <option>Montserrat</option>
      <option>Oswald</option>
      <option>Raleway</option>
      <option>Poppins</option>
      <option>Ubuntu</option>
      <option>Playfair Display</option>
      <option>Merriweather</option>
      <option>PT Sans</option>
      <option>Source Sans Pro</option>
      <option>Cinzel</option>
      <option>Bebas Neue</option>
      <option>Lobster</option>
      <option>Pacifico</option>
      <option>Dancing Script</option>
      <option>Abril Fatface</option>
      <option>Anton</option>
      <option>Archivo Black</option>
    </select>
  </div>
);

export const SizeControl = ({
  value,
  pendingValue,
  disabled,
  editing,
  editValue,
  onStartEdit,
  onEditChange,
  onEditBlur,
  onEditKeyDown,
  onRangeChange,
  onRangePointerUp,
  max,
  getMaxFontSize,
  handleDragEnd,
}) => (
  <div className="control-group">
    <div className="control-label">Size</div>
    <div className="range-with-value">
      <input
        type="range"
        min="10"
        max={max}
        step="1"
        value={pendingValue ?? value}
        disabled={disabled}
        onChange={onRangeChange}
        onPointerUp={() => {
          onRangePointerUp();
          if (handleDragEnd) handleDragEnd();
        }}
      />
      {editing ? (
        <input
          type="number"
          className="range-value-badge"
          autoFocus
          min={10}
          max={getMaxFontSize()}
          value={editValue}
          onChange={onEditChange}
          onBlur={() => onEditBlur()}
          onKeyDown={onEditKeyDown}
          style={{
            width: 60,
            textAlign: 'center',
          }}
        />
      ) : (
        <div
          className="range-value-badge"
          onDoubleClick={onStartEdit}
        >
          {Math.round(value || 16)}px
        </div>
      )}
    </div>
  </div>
);

export const TextStyleControls = ({
  textId,
  item,
  disabled,
  onUpdate,
  onTransform,
  onAlign,
  showUnderline = true,
  showAlignButtons = true,
  isNumberText = false,
  buttonsClassName = 'text-style-buttons',
  alignDisabled,
}) => {
  const uppercaseActive = item?.textTransform === 'uppercase';
  const lowercaseActive = item?.textTransform === 'lowercase';
  const transformClass = (uppercaseActive || lowercaseActive) ? 'toggle-letter' : '';

  return (
    <div className="control-group">
      <div className="control-label">Style</div>
      <div className={buttonsClassName}>
        <button
          type="button"
          className={`toggle-button ${transformClass}`}
          disabled={disabled || isNumberText}
          onClick={() => onTransform(textId, item?.textTransform === 'uppercase' ? 'lowercase' : item?.textTransform === 'lowercase' ? 'none' : 'uppercase')}
        >
          {uppercaseActive ? '𝐚' : lowercaseActive ? '𝐚𝐀' : '𝐀'}
        </button>
        <button
          type="button"
          className={`toggle-button ${item?.bold ? 'active' : ''}`}
          onClick={() => onUpdate(textId, 'bold', !item?.bold)}
          disabled={disabled}
        >
          <span className="toggle-letter">B</span>
        </button>
        <button
          type="button"
          className={`toggle-button ${item?.italic ? 'active' : ''}`}
          onClick={() => onUpdate(textId, 'italic', !item?.italic)}
          disabled={disabled}
        >
          <span className="toggle-letter">𝐼</span>
        </button>
        {showUnderline && (
          <button
            type="button"
            className={`toggle-button ${item?.underline ? 'active' : ''}`}
            onClick={() => onUpdate(textId, 'underline', !item?.underline)}
            disabled={disabled}
          >
            <span className="toggle-letter">U̲</span>
          </button>
        )}
        {showAlignButtons && (
          <>
            <button
              type="button"
              className={item?.textAlign === "left" ? "toggle-button text-transform-button active" : "text-transform-button toggle-button"}
              disabled={alignDisabled ?? disabled}
              onClick={() => onAlign(textId, "left")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="#80808082"><path d="M384 128C384 145.7 369.7 160 352 160L128 160C110.3 160 96 145.7 96 128C96 110.3 110.3 96 128 96L352 96C369.7 96 384 110.3 384 128zM384 384C384 401.7 369.7 416 352 416L128 416C110.3 416 96 401.7 96 384C96 366.3 110.3 352 128 352L352 352C369.7 352 384 366.3 384 384zM96 256C96 238.3 110.3 224 128 224L512 224C529.7 224 544 238.3 544 256C544 273.7 529.7 288 512 288L128 288C110.3 288 96 273.7 96 256zM544 512C544 529.7 529.7 544 512 544L128 544C110.3 544 96 529.7 96 512C96 494.3 110.3 480 128 480L512 480C529.7 480 544 494.3 544 512z" /></svg>
            </button>
            <button
              type="button"
              className={item?.textAlign === "center" ? "toggle-button text-transform-button active" : "text-transform-button toggle-button"}
              disabled={alignDisabled ?? disabled}
              onClick={() => onAlign(textId, "center")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="#80808082"><path d="M448 128C448 110.3 433.7 96 416 96L224 96C206.3 96 192 110.3 192 128C192 145.7 206.3 160 224 160L416 160C433.7 160 448 145.7 448 128zM544 256C544 238.3 529.7 224 512 224L128 224C110.3 224 96 238.3 96 256C96 273.7 110.3 288 128 288L512 288C529.7 288 544 273.7 544 256zM96 512C96 529.7 110.3 544 128 544L512 544C529.7 544 544 529.7 544 512C544 494.3 529.7 480 512 480L128 480C529.7 480 544 494.3 544 512zM448 384C448 366.3 433.7 352 416 352L224 352C206.3 352 192 366.3 192 384C192 401.7 206.3 416 224 416L416 416C433.7 416 448 401.7 448 384z" /></svg>
            </button>
            <button
              type="button"
              className={item?.textAlign === "right" ? "toggle-button text-transform-button active" : "text-transform-button toggle-button"}
              disabled={alignDisabled ?? disabled}
              onClick={() => onAlign(textId, "right")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="#80808082"><path d="M544 128C544 145.7 529.7 160 512 160L288 160C270.3 160 256 145.7 256 128C256 110.3 270.3 96 288 96L512 96C529.7 96 544 110.3 544 128zM544 384C544 401.7 529.7 416 512 416L288 416C270.3 416 256 401.7 256 384C256 366.3 270.3 352 288 352L512 352C529.7 352 544 366.3 544 384zM96 256C96 273.7 110.3 288 128 288L512 288C529.7 288 544 273.7 544 256C544 238.3 529.7 224 512 224L128 224C110.3 224 96 273.7 96 256zM544 512C544 529.7 529.7 544 512 544L128 544C110.3 544 96 529.7 96 512C96 529.7 544 128 544L512 544C529.7 544 544 529.7 544 512z" /></svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const TextColorControl = ({
  textId,
  value,
  disabled,
  onChange,
  allTextColors,
  DEFAULT_TEXT_COLORS,
  customColors,
  setCustomColors,
  colorInputRef,
  showCustomInput,
  setShowCustomInput,
}) => (
  <div className="control-group">
    <div className="control-label">Color</div>
    <ColorSwatchPicker
      idPrefix={textId}
      value={value}
      disabled={disabled}
      onChange={onChange}
      allTextColors={allTextColors}
      DEFAULT_TEXT_COLORS={DEFAULT_TEXT_COLORS}
      customColors={customColors}
      setCustomColors={setCustomColors}
      colorInputRef={colorInputRef}
      showCustomInput={showCustomInput}
      setShowCustomInput={setShowCustomInput}
    />
  </div>
);
